// src/liveFutures.js — the ONE set of rules for live futures on the Live Bids
// board (2026-10-30). Shared by the receiving endpoint (api/_live-futures.js)
// and the page (LiveBidBoard in App.jsx), so a price can never be read one way
// on the way in and another way on screen.
//
// Kevin, 2026-10-30: "How can you help me quote live grain prices. We need
// futures and basis ... I have to look in app, then look at basis sheet, then do
// the math to get a bid." For his own use only ("Just for me"), so the quotes
// are never shown to a grower: see sql/118.
//
// The office PC reads CQG through Excel and posts what it sees. CQG can hand a
// grain price over as cents (425.25), as cents with eighths (425'2), or as
// dollars (4.2525) depending on how the cell is formatted, so every price is
// normalised here to DOLLARS PER BUSHEL before it is stored. No grain has ever
// traded at $100/bu, so a number of 100 or more is cents.

const ROOT_FOR_COMMODITY = { corn: "ZC", soybeans: "ZS", soy: "ZS", plenish: "ZS", wheat: "ZW" };
const ROOT_LABEL = { ZC: "Corn", ZS: "Soybeans", ZW: "Wheat (Chicago SRW)" };
// Outside these the number is a misread cell, not a price. Wide on purpose.
const SANE_DOLLARS = { ZC: [1, 15], ZS: [4, 40], ZW: [2, 25] };
const MONTHS = "FGHJKMNQUVXZ";
const MONTH_NAME = { F: "Jan", G: "Feb", H: "Mar", J: "Apr", K: "May", M: "Jun", N: "Jul", Q: "Aug", U: "Sep", V: "Oct", X: "Nov", Z: "Dec" };

// 'corn', 'Z26' -> 'ZCZ26'. Null when either half is not recognised.
function symbolFor(commodity, cme) {
  const root = ROOT_FOR_COMMODITY[String(commodity || "").toLowerCase().trim()];
  const m = String(cme || "").toUpperCase().trim().match(/^([FGHJKMNQUVXZ])(\d{2})$/);
  return root && m ? `${root}${m[1]}${m[2]}` : null;
}

// CQG names the CBOT grains ZCE, ZSE and ZWA (ZCEZ26 = Corn Dec 26).
const CQG_ROOT = { ZC: "ZCE", ZS: "ZSE", ZW: "ZWA" };
const ROOT_FROM_CQG = { ZCE: "ZC", ZSE: "ZS", ZWA: "ZW" };

// 'ZCZ26' or CQG's 'ZCEZ26' -> { symbol:'ZCZ26', root:'ZC', month:'Z', yy:'26', label:'Corn Dec 26' }
function parseSymbol(sym) {
  const m = String(sym || "").toUpperCase().trim().match(/^(ZCE|ZSE|ZWA|ZC|ZS|ZW)([FGHJKMNQUVXZ])(\d{2})$/);
  if (!m) return null;
  const root = ROOT_FROM_CQG[m[1]] || m[1];
  return { symbol: `${root}${m[2]}${m[3]}`, root, month: m[2], yy: m[3],
           label: `${ROOT_LABEL[root].split(" ")[0]} ${MONTH_NAME[m[2]]} ${m[3]}` };
}

// 'ZCZ26' -> 'ZCEZ26', the name to type into the CQG XL Toolkit.
function cqgSymbol(sym) {
  const p = parseSymbol(sym);
  return p ? `${CQG_ROOT[p.root]}${p.month}${p.yy}` : null;
}

// Any CQG price shape -> dollars/bu, or null. "425'2" is 425 and 2/8 cents.
function parsePrice(v) {
  if (v == null || v === "") return null;
  let n;
  if (typeof v === "number") n = v;
  else {
    const s = String(v).replace(/[,$\s]/g, "");
    const frac = s.match(/^(-?\d+)'(\d)$/);
    if (frac) n = Number(frac[1]) + Math.sign(Number(frac[1]) || 1) * Number(frac[2]) / 8;
    else if (/^-?\d+(\.\d+)?$/.test(s)) n = Number(s);
    else return null;
  }
  if (!isFinite(n)) return null;
  return Math.abs(n) >= 100 ? n / 100 : n;
}

// A net change is small, so it cannot be told apart by size: the PC says which
// unit it sent. "c" = cents (CQG's native grain unit), anything else = dollars.
function parseChange(v, unit) {
  if (v == null || v === "") return null;
  let n;
  if (typeof v === "number") n = v;
  else {
    const s = String(v).replace(/[,$\s+]/g, "");
    const frac = s.match(/^(-?)(\d+)'(\d)$/);
    if (frac) n = (frac[1] ? -1 : 1) * (Number(frac[2]) + Number(frac[3]) / 8);
    else if (/^-?\d+(\.\d+)?$/.test(s)) n = Number(s);
    else return null;
  }
  if (!isFinite(n)) return null;
  return unit === "c" ? n / 100 : n;
}

function isSane(root, dollars) {
  const r = SANE_DOLLARS[root];
  return !!r && dollars != null && dollars >= r[0] && dollars <= r[1];
}

// CBOT grain hours, Central time: Sun-Fri 7:00pm-7:45am and Mon-Fri 8:30am-1:20pm.
// Exchange holidays are not modelled: on one, the board reads "no update" during
// those hours rather than "closed", which errs toward warning.
function grainMarketOpen(date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: "America/Chicago", weekday: "short", hour: "numeric", minute: "numeric", hour12: false }).formatToParts(date);
  const get = (t) => parts.find((p) => p.type === t)?.value;
  const day = get("weekday");
  const mins = (Number(get("hour")) % 24) * 60 + Number(get("minute"));
  const day1 = mins >= 8 * 60 + 30 && mins < 13 * 60 + 20;   // day session
  const night = mins >= 19 * 60 || mins < 7 * 60 + 45;       // overnight session
  if (day === "Sat") return false;
  if (day === "Sun") return mins >= 19 * 60;
  if (day === "Fri") return day1 || mins < 7 * 60 + 45;
  return day1 || night;
}

// How old a quote may be, while the market is open, before the board calls it stale.
const STALE_AFTER_MS = 3 * 60 * 1000;

function freshness(receivedAt, now = new Date()) {
  if (!receivedAt) return { state: "none", ageMs: null };
  const ageMs = now - new Date(receivedAt);
  if (!grainMarketOpen(now)) return { state: "closed", ageMs };
  return { state: ageMs > STALE_AFTER_MS ? "stale" : "live", ageMs };
}

const round4 = (n) => (n == null || !isFinite(n) ? null : Math.round(n * 10000) / 10000);
function bid(futures, basis) {
  if (futures == null || basis == null) return null;
  return round4(Number(futures) + Number(basis));
}

module.exports = { ROOT_FOR_COMMODITY, ROOT_LABEL, MONTHS, MONTH_NAME, STALE_AFTER_MS,
                   symbolFor, parseSymbol, cqgSymbol, parsePrice, parseChange, isSane,
                   grainMarketOpen, freshness, bid, round4 };
